import { useCallback, useState } from 'react'
import type { ConfirmDialogOptions } from '@/contexts/confirm-dialog-context'
import { ConfirmDialog } from '@/components/common/confirm-dialog'
import { ConfirmDialogContext } from '@/contexts/confirm-dialog-context'

export function ConfirmDialogProvider({
  children,
}: {
  children: React.ReactNode
}) {
  const [dialogState, setDialogState] = useState<{
    open: boolean
    options: ConfirmDialogOptions | null
  }>({
    open: false,
    options: null,
  })
  const [isLoading, setIsLoading] = useState(false)

  const showConfirmDialog = useCallback((options: ConfirmDialogOptions) => {
    setDialogState({
      open: true,
      options,
    })
  }, [])

  const handleConfirm = async () => {
    if (dialogState.options?.onConfirm) {
      setIsLoading(true)
      try {
        await dialogState.options.onConfirm()
        setDialogState({ open: false, options: null })
      } finally {
        setIsLoading(false)
      }
    }
  }

  const handleCancel = () => {
    dialogState.options?.onCancel?.()
    setDialogState({ open: false, options: null })
  }

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      handleCancel()
    }
  }

  return (
    <ConfirmDialogContext.Provider value={{ showConfirmDialog }}>
      {children}
      {dialogState.options && (
        <ConfirmDialog
          open={dialogState.open}
          onOpenChange={handleOpenChange}
          title={dialogState.options.title}
          description={dialogState.options.description}
          confirmText={dialogState.options.confirmText}
          cancelText={dialogState.options.cancelText}
          variant={dialogState.options.variant}
          requireInput={dialogState.options.requireInput}
          onConfirm={handleConfirm}
          onCancel={handleCancel}
          isLoading={isLoading}
        />
      )}
    </ConfirmDialogContext.Provider>
  )
}
